import { Checkbox } from '@HoudiniUI/Checkbox';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@HoudiniUI/Field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import { css } from 'aphrodite';
import { useEffect, useMemo, useState } from 'react';
import { IAICampaignBrief, IAICampaignQuota, availableForBrief } from '../../../../models/AICampaign';
import { AIPromptUsage } from '../../../../models/AIPromptTemplate';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useAICampaignQuota } from '../../../../queries/AICampaign';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { AIPromptField, IAIPromptFieldCopy, useAIPromptField } from '../../../../shared/components/ai/AIPromptField';
import { getGoalExample } from './goalExamples';
import { styleSheet } from './styles';

interface IProps {
	brief?: IAICampaignBrief;
	/**
	 * Reports the current brief on every edit, or null while it is not yet valid. Navigation lives with the caller;
	 * `problem` is set when there is something more specific to tell the user than that the goal is missing.
	 */
	onBriefChange: (brief: IAICampaignBrief | null, problem?: string | null) => void;
	/** Who the emails go out as; the caller owns that choice, the brief only gives it its place in the form. */
	sendFrom?: React.ReactNode;
}

// A prompt fills the goal only. Web research and the recency threshold describe this send, so they stay as the user
// set them rather than following whichever prompt was picked, and are never saved with it.
const GOAL_PROMPT_COPY: Partial<IAIPromptFieldCopy> = {
	attributionNote: 'Editing the goal drops this.',
	panelDescription: 'Using one fills the goal. Web research and recency stay as you set them.',
	panelReplaceDescription: 'Using one replaces what you have written. Web research and recency stay as you set them.',
	saveDescription: 'Keep this goal so you can start the next campaign from it.',
	saveNamePlaceholder: 'Year-end renewal review',
	saveNote: 'Only the goal. Web research and the recency setting stay a choice for each campaign.',
	savedEmptyText: 'Nobody here has saved a prompt yet. Write a goal and use Save as a prompt beside it.',
};

const SKIP_NOBODY = 'none';
const SKIP_DAY_CHOICES = [7, 14, 30, 60, 90];

/** The recency choices, keeping a brief's own threshold even when it is not one of the standard ones. */
const skipDayItems = (current: number | null) => [
	{ label: 'No one: write to everyone', value: SKIP_NOBODY },
	...[...new Set([...SKIP_DAY_CHOICES, ...(current ? [current] : [])])]
		.sort((a, b) => a - b)
		.map(days => ({ label: `${days} days`, value: String(days) })),
];

export const EditAICampaignBrief: React.FC<IProps> = ({ brief, onBriefChange, sendFrom }) => {
	const [goal, setGoal] = useState(brief?.goal ?? '');
	// Roughly ten times the cost and noticeably slower, so it must be opted into rather than defaulted on.
	const [webSearchEnabled, setWebSearchEnabled] = useState(brief?.webSearchEnabled ?? false);
	const [skipDays, setSkipDays] = useState<number | null>(brief?.skipRecentlyContactedDays || null);
	// Built from the mount-time seed only, so a returning brief's own threshold stays offered after it is changed.
	const [skipItems] = useState(() => skipDayItems(brief?.skipRecentlyContactedDays || null));

	const { data: quota, isLoading: quotaLoading } = useAICampaignQuota();
	const userSession = useUserSession();
	const goalExample = getGoalExample(userSession?.account?.additionalInfo?.industry);
	const { logEvent } = useEventLogging('EditAICampaignBrief');
	const goalField = useAIPromptField({
		onChange: setGoal,
		onPromptApplied: ({ prompt }) => logEvent('SamplePromptApplied', { promptId: prompt.id }),
		value: goal,
	});
	const { placeholderProblem, unfilledPlaceholder } = goalField;

	const available = useMemo(() => availableForBrief(quota, webSearchEnabled), [quota, webSearchEnabled]);

	const isValid = goal.trim().length > 0 && !unfilledPlaceholder && (available === undefined || available > 0);

	const sourceTemplateId = goalField.sourcePrompt?.id ?? null;

	useEffect(() => {
		onBriefChange(
			isValid
				? {
						...brief,
						goal: goal.trim(),
						webSearchEnabled,
						skipRecentlyContactedDays: skipDays,
						// Written on every report, null included: the spread above carries the previous value forward, so
						// omitting it would leave a rewritten goal still credited to the prompt it started from.
						sourceTemplateId,
					}
				: null,
			placeholderProblem
		);
		// `brief` is only the mount-time seed for the fields; reacting to it would echo our own reports back around.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [goal, webSearchEnabled, skipDays, isValid, onBriefChange, placeholderProblem, sourceTemplateId]);

	return (
		<div className={css(styleSheet.container)}>
			<div>
				<h2 className={css(styleSheet.heading)}>What should these emails accomplish?</h2>
				<p className={css(styleSheet.description)}>
					Describe the goal. Each recipient gets their own email, written from what Levitate already knows about them.
				</p>
			</div>

			<FieldGroup>
				{sendFrom ? (
					<Field>
						<FieldLabel id='ai-campaign-send-from-label'>Send from</FieldLabel>
						<div aria-labelledby='ai-campaign-send-from-label' role='group'>
							{sendFrom}
						</div>
					</Field>
				) : null}
				<Field>
					<FieldLabel htmlFor='ai-campaign-goal'>What each email should say</FieldLabel>
					<AIPromptField
						copy={GOAL_PROMPT_COPY}
						field={goalField}
						id='ai-campaign-goal'
						placeholder={goalExample}
						textAreaStyles={[styleSheet.goalTextArea]}
						usage={AIPromptUsage.EmailCampaign}
					/>
					<FieldDescription>
						The purpose of the campaign, in your words. Anything you would tell a colleague writing these by hand
						belongs here. It also decides who is left out: a recipient the goal plainly does not apply to is skipped.
					</FieldDescription>
				</Field>

				<section aria-labelledby='ai-campaign-send-settings' className={css(styleSheet.settings)}>
					<h3 className={css(styleSheet.settingsTitle)} id='ai-campaign-send-settings'>
						Send settings
					</h3>
					<div className={css(styleSheet.settingsFields)}>
						{/* Research instructions still exist on the brief API-side, but the form no longer collects them: what
			    belongs there versus in the goal was too confusing to explain, and the goal alone carries it. */}
						<Field>
							<div className={css(styleSheet.researchToggleRow)}>
								<Checkbox
									checked={webSearchEnabled}
									id='ai-campaign-web-search'
									onCheckedChange={checked => setWebSearchEnabled(checked === true)}
								/>
								<div className={css(styleSheet.toggleCopy)}>
									<FieldLabel htmlFor='ai-campaign-web-search'>Also allow searching the web</FieldLabel>
									<FieldDescription>
										Only needed when the emails depend on something Levitate does not already hold. It makes generation
										slower and draws on a separate, smaller monthly allowance, so leave it off for campaigns built from
										your own data such as policy renewals or service history.
									</FieldDescription>
								</div>
							</div>
						</Field>

						<Field>
							<FieldLabel htmlFor='ai-campaign-skip-recent'>Skip anyone contacted in the last</FieldLabel>
							<Select
								items={skipItems}
								onValueChange={value => setSkipDays(value && value !== SKIP_NOBODY ? Number(value) : null)}
								value={skipDays ? String(skipDays) : SKIP_NOBODY}
							>
								<SelectTrigger id='ai-campaign-skip-recent' styles={[styleSheet.skipTrigger]}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{skipItems.map(item => (
										<SelectItem key={item.value} value={item.value}>
											{item.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FieldDescription>
								The AI weighs recent contact when deciding whether someone should be left out of this campaign.
							</FieldDescription>
						</Field>
					</div>
				</section>
			</FieldGroup>

			<QuotaSummary loading={quotaLoading} quota={quota} webSearchEnabled={webSearchEnabled} />
		</div>
	);
};

const QuotaSummary: React.FC<{
	loading: boolean;
	quota?: IAICampaignQuota;
	webSearchEnabled: boolean;
}> = ({ loading, quota, webSearchEnabled }) => {
	if (loading) {
		return (
			<div className={css(styleSheet.quotaLine)}>
				<LoadingSpinner type='small' />
			</div>
		);
	}

	if (!quota) {
		return null;
	}

	const available = availableForBrief(quota, webSearchEnabled) ?? 0;
	const exhausted = available <= 0;
	const used = webSearchEnabled
		? `${quota.webResearchUsed.toLocaleString()} of ${quota.webResearchLimit.toLocaleString()} with web research used this month`
		: `${quota.totalUsed.toLocaleString()} of ${quota.totalLimit.toLocaleString()} AI emails used this month`;

	return (
		<div className={css(styleSheet.quotaLine)}>
			<p className={css(styleSheet.quotaNote, exhausted ? styleSheet.quotaExhausted : undefined)}>
				{exhausted
					? `No AI emails left this month · ${used}`
					: `Up to ${available.toLocaleString()} emails in this campaign · ${used}`}
			</p>
			{exhausted && webSearchEnabled && quota.availableWithoutWebResearch > 0 && (
				<p className={css(styleSheet.quotaNote)}>
					Turning off web research would let this campaign use the wider allowance instead.
				</p>
			)}
		</div>
	);
};
